import { useNavigate } from 'react-router';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import textsData from '@/textData/DashboardTexts.json';
import { pathURL } from '@/components/constants/urls';
import { useUserLanguage } from '@/customHooks';
import type { IncomeAndExpensesOutput } from '@/graphql/generated';
import { DashboardCardPeriodCaption } from '../atoms';

type DashboardIncomeAndExpensesCardProps = {
  data: IncomeAndExpensesOutput | undefined;
};

function formatAmount(value: string | undefined): string {
  if (!value) return '$0.00';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(num);
}

const INCOME_COLOR = '#53BA74';
const EXPENSE_COLOR = '#FF7878';

const expensesReportPath = `/${pathURL.USER}/${pathURL.REPORTS}/${pathURL.EXPENSES}`;

export const DashboardIncomeAndExpensesCard = ({
  data,
}: DashboardIncomeAndExpensesCardProps) => {
  const navigate = useNavigate();
  const userLanguage = useUserLanguage();
  const texts =
    userLanguage === 'es'
      ? textsData.es.incomeAndExpensesCard
      : textsData.en.incomeAndExpensesCard;

  const income = data?.income;
  const expenses = data?.expenses;

  const incomeChartData = [
    { name: 'collected', value: income?.collectedPercentage ?? 0, color: INCOME_COLOR },
    {
      name: 'notCollected',
      value: Math.max(0, 100 - (income?.collectedPercentage ?? 0)),
      color: '#e0e0e0',
    },
  ];

  const expensesChartData = [
    { name: 'paid', value: expenses?.paidPercentage ?? 0, color: EXPENSE_COLOR },
    {
      name: 'notPaid',
      value: Math.max(0, 100 - (expenses?.paidPercentage ?? 0)),
      color: '#e0e0e0',
    },
  ];

  return (
    <div className="box-border h-full min-h-[420px] rounded-xl bg-white px-6 pb-5 pt-4 shadow-[0_14px_32px_rgba(15,23,42,0.16)]">
      <div className="mb-[0.35rem] flex flex-col items-start">
        <h3 className="m-0 text-[20px] font-bold text-[#1d2f35]">{texts.title}</h3>
        <DashboardCardPeriodCaption tightTop />
      </div>
      <hr className="mb-4 mt-[0.4rem] border-0 border-t border-t-[#E5E7EB]" />

      <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-12">
        <div className="md:col-span-5">
          <div className="relative h-[172px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={incomeChartData}
                  dataKey="value"
                  innerRadius={58}
                  outerRadius={76}
                  paddingAngle={0}
                  startAngle={90}
                  endAngle={-270}
                >
                  {incomeChartData.map((entry, index) => (
                    <Cell key={`income-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute left-1/2 top-1/2 z-[1] flex -translate-x-1/2 -translate-y-1/2 items-center justify-center text-center font-[Montserrat,Arial,sans-serif] text-[16.4px] font-bold text-gray-800">
              {`${(income?.collectedPercentage ?? 0).toFixed(2)} %`}
            </div>
          </div>
        </div>
        <div className="md:col-span-7">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="m-0 font-[Montserrat,Arial,sans-serif] text-base font-bold text-[#545454]">{texts.income}</h4>
            <button
              className="border-none bg-transparent p-0 font-[Montserrat,Arial,sans-serif] text-sm font-bold text-[#4A9462] hover:underline"
              type="button"
              onClick={() => navigate(expensesReportPath)}
            >
              {texts.seeIncome}
            </button>
          </div>
          <div className="rounded-[14px] border border-[rgba(0,0,0,0.12)] bg-[#FAFAFA] px-4 py-[0.2rem]">
            <div className="flex items-center justify-between border-b border-b-[rgba(0,0,0,0.12)] py-[0.65rem] text-base">
              <span className="font-[Montserrat,Arial,sans-serif] font-medium text-[#545454]">{texts.collected}</span>
              <span className="font-[Montserrat,Arial,sans-serif] font-medium text-[#545454]">{formatAmount(income?.collected)}</span>
            </div>
            <div className="flex items-center justify-between border-b border-b-[rgba(0,0,0,0.12)] py-[0.65rem] text-base">
              <span className="font-[Montserrat,Arial,sans-serif] font-medium text-[#545454]">{texts.notCollected}</span>
              <span className="font-[Montserrat,Arial,sans-serif] font-medium text-[#FF7878]">{formatAmount(income?.notCollected)}</span>
            </div>
            <div className="flex items-center justify-between pb-[0.65rem] pt-[0.7rem] text-base">
              <span className="font-[Montserrat,Arial,sans-serif] font-medium text-[#545454]">{texts.total}</span>
              <span className="font-[Montserrat,Arial,sans-serif] font-bold text-[#545454]">{formatAmount(income?.total)}</span>
            </div>
          </div>
        </div>
      </div>

      <hr className="mb-[0.95rem] mt-[1.1rem] border-0 border-t border-t-[#E5E7EB]" />

      <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-12">
        <div className="md:col-span-5">
          <div className="relative h-[172px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={expensesChartData}
                  dataKey="value"
                  innerRadius={58}
                  outerRadius={76}
                  paddingAngle={0}
                  startAngle={90}
                  endAngle={-270}
                >
                  {expensesChartData.map((entry, index) => (
                    <Cell key={`expenses-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute left-1/2 top-1/2 z-[1] flex -translate-x-1/2 -translate-y-1/2 items-center justify-center text-center font-[Montserrat,Arial,sans-serif] text-[16.4px] font-bold text-gray-800">
              {`${(expenses?.paidPercentage ?? 0).toFixed(0)} %`}
            </div>
          </div>
        </div>
        <div className="md:col-span-7">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="m-0 font-[Montserrat,Arial,sans-serif] text-base font-bold text-[#545454]">{texts.expenses}</h4>
            <button className="border-none bg-transparent p-0 font-[Montserrat,Arial,sans-serif] text-sm font-bold text-[#4A9462] hover:underline" type="button" onClick={() => navigate(expensesReportPath)}>
              {texts.seeExpenses}
            </button>
          </div>
          <div className="rounded-[14px] border border-[rgba(0,0,0,0.12)] bg-[#FAFAFA] px-4 py-[0.2rem]">
            <div className="flex items-center justify-between border-b border-b-[rgba(0,0,0,0.12)] py-[0.65rem] text-base">
              <span className="font-[Montserrat,Arial,sans-serif] font-medium text-[#545454]">{texts.paid}</span>
              <span className="font-[Montserrat,Arial,sans-serif] font-medium text-[#545454]">{formatAmount(expenses?.paid)}</span>
            </div>
            <div className="flex items-center justify-between border-b border-b-[rgba(0,0,0,0.12)] py-[0.65rem] text-base">
              <span className="font-[Montserrat,Arial,sans-serif] font-medium text-[#545454]">{texts.notPaid}</span>
              <span className="font-[Montserrat,Arial,sans-serif] font-medium text-[#FF7878]">{formatAmount(expenses?.notPaid)}</span>
            </div>
            <div className="flex items-center justify-between pb-[0.65rem] pt-[0.7rem] text-base">
              <span className="font-[Montserrat,Arial,sans-serif] font-medium text-[#545454]">{texts.total}</span>
              <span className="font-[Montserrat,Arial,sans-serif] font-bold text-[#545454]">{formatAmount(expenses?.total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
